import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import Pagination from '@mui/material/Pagination';
import {
    getSearchInputData,
    sortMsisdnsList,
    sortGroupsIdsList,
    getOrLoadUserDetails,
    groupHasTransmissionDenied,
    updateContactsSelectorData,
    getContactsSelectorData,
    selectUsersListFilters,
    contactsSelectorIsRegistered,
    registerContactsSelector,
    unregisterContactsSelector,
    searchContacts,
    requestSearchInputPage,
    findUsersByMsisdnList,
    findGroupsByIdsList,
} from 'modules/contacts/stores/contacts-slice-handler';
import ContactMenu from './contact-menu';
import ContactSearchInput from './contact-search-input';
import { TabTypes } from '../../../constants/user-groups-constants';
import ContactsTabsSystem from './contacts-tabs-system';
import ContactUserItem from './contact-user-item';
import t from '../../../utils/translate';
import ContactGroupItem from './contact-group-item';
import { mergeDeep, offsetFromPage } from '../../../utils/functions';
import { contactsIdsListIsEmpty, contactsRecordsOnPage, searchInputHasNoResults } from '../../../utils/contact-util';
import Preloader from '../../shared/components/preloader';

/**
 * This is a simple component which is providing three things:
 * - search input
 * - tabs system for Users and Groups
 * - users list and groups list
 * @param {string} contactsSelectorUuid The id which allows finding the contacts selector under the store.
 * @param {ContactsSelectorOptions} options
 * @param {object} additionalContent Additional content to be rendered in the dedicated container between search input and tabs system.
 * @constructor
 */
export default function ContactsSelector({ contactsSelectorUuid, options, additionalContent }) {
    // make sure that the selector is registered
    if (!contactsSelectorIsRegistered(contactsSelectorUuid)) {
        registerContactsSelector(contactsSelectorUuid);
    }

    /**
     * @type {ContactsSelectorOptions}
     */
    const defaultOptions = {
        applyUsersListFilters: false,
        lockSelectedContacts: false,
        multipleSelectionOpen: false,
        onContactNameClick: () => false,
        menuType: ContactMenu.NONE,
        withFavoriteControls: false,
        displayOnlyUsers: false,
        searchOnlyMode: false,
        recordsOnPage: contactsRecordsOnPage(),
        groups: {
            restrictions: {
                transmissionNotDenied: false,
            },
            selectedGroupsIds: [],
        },
        users: {
            renderingOptions: {
                withStatusDetails: false,
            },
            restrictions: {
                allowGuests: true,
                onlyHumans: true,
            },
            selectedMsisdns: [],
            ignoredMsisdns: [],
        },
    };

    options = mergeDeep(defaultOptions, options);

    const [dataLoading, setDataLoading] = useState(false);

    const [usersSearchInputId, setUsersSearchInputId] = useState(null);
    const [groupsSearchInputId, setGroupsSearchInputId] = useState(null);

    const [currentUsersSelection, setCurrentUsersSelection] = useState([]);
    const [currentGroupsSelection, setCurrentGroupsSelection] = useState([]);

    // these are set with null, in order to determine if there was no request made yet
    const [loadedUsers, setLoadedUsers] = useState(null);
    const [loadedGroups, setLoadedGroups] = useState(null);
    const [totalUsers, setTotalUsers] = useState(0);
    const [totalGroups, setTotalGroups] = useState(0);

    const searchDataUsers = useSelector(() => getSearchInputData(usersSearchInputId));
    const searchDataGroups = useSelector(() => getSearchInputData(groupsSearchInputId));

    // unregister the selector when finished
    useEffect(() => {
        return () => {
            if (typeof options.unregisterWhenRemoved === 'undefined' || options.unregisterWhenRemoved !== false) {
                unregisterContactsSelector(contactsSelectorUuid);
            }
        };
    }, []);

    /**
     * @type {UsersListFilters}
     */
    const userListFilters = useSelector(selectUsersListFilters);

    useEffect(() => {
        if (!options.applyUsersListFilters) {
            return;
        }

        // filter changed => reload the list
        handleRecordsPage(1);
    }, [userListFilters]);

    /**
     * @type {ContactsSelectorData}
     */
    const data = useSelector(() => getContactsSelectorData(contactsSelectorUuid));
    const currentTab = data.currentTab;
    const usersPage = data.usersPage || 1;
    const groupsPage = data.groupsPage || 1;
    const availableMsisdnList = options.users.availableMsisdns ? options.users.availableMsisdns.join('') : '';
    const availableGroupsIdsList = options.groups.availableGroupsIds ? options.groups.availableGroupsIds.join('') : '';

    const setCurrentTab = (tab) => {
        updateContactsSelectorData(contactsSelectorUuid, {
            currentTab: tab,
        });
    };

    /**
     * @param {ContactsSelectorData} data
     */
    const propagateSelectionChangedData = (data) => {
        updateContactsSelectorData(contactsSelectorUuid, data);

        if (typeof options.onSelectionChanged === 'function') {
            options.onSelectionChanged(data);
        }
    };

    useEffect(() => {
        if (options.displayOnlyUsers === true) {
            setCurrentTab(TabTypes.USERS_TAB);
        }

        setUsersSearchInputId(data.usersSearchInputId);
        setGroupsSearchInputId(data.groupsSearchInputId);

        setCurrentUsersSelection([...options.users.selectedMsisdns]);
        setCurrentGroupsSelection([...options.groups.selectedGroupsIds]);

        if (options.users.selectedMsisdns.length > 0 || options.groups.selectedGroupsIds.length > 0) {
            setTimeout(() => {
                propagateSelectionChangedData({
                    msisdns: sortMsisdnsList(options.users.selectedMsisdns),
                    groupsIds: sortGroupsIdsList(options.groups.selectedGroupsIds),
                });
            });
        }
    }, []);

    useEffect(() => {
        setCurrentUsersSelection([...data.msisdns]);
        setCurrentGroupsSelection([...data.groupsIds]);
    }, [data.msisdns, data.groupsIds]);

    useEffect(() => {
        handleRecordsPage();
    }, [data.currentTab]);

    useEffect(() => {
        if (
            currentTab &&
            TabTypes.USERS_TAB &&
            (!options.users.availableMsisdns || options.users.availableMsisdns.length === 0)
        ) {
            return;
        }

        if (
            currentTab &&
            TabTypes.GROUPS_TAB &&
            (!options.groups.availableGroupsIds || options.groups.availableGroupsIds.length === 0)
        ) {
            return;
        }

        handleRecordsPage();
    }, [availableMsisdnList, availableGroupsIdsList]);

    useEffect(() => {
        if (data.refreshRequired === true) {
            // reset this flag
            updateContactsSelectorData(contactsSelectorUuid, { refreshRequired: false });
            handleRecordsPage();
        } else {
            // nothing to do; flag was reset
        }
    }, [data.refreshRequired]);

    /**
     * Load the records from this page.
     * @param {number|null} page If is null, then is the current page from the selected tab.
     */
    const handleRecordsPage = (page = null) => {
        if (currentTab === TabTypes.USERS_TAB) {
            if (options.searchOnlyMode && (!searchDataUsers || searchDataUsers.keyword.length == 0)) {
                return;
            }

            if (options.users.availableMsisdns) {
                if (options.users.availableMsisdns.length === 0) {
                    return;
                }

                setDataLoading(true);

                findUsersByMsisdnList(options.users.availableMsisdns).then((data) => {
                    setLoadedUsers([...data.msisdns]);
                    setTotalUsers(data.totalRecordsCount);
                    setDataLoading(false);
                });

                return;
            }

            if (searchDataUsers && searchDataUsers.keyword.length > 0) {
                if (page === null) {
                    page = searchDataUsers.page;
                }

                requestSearchInputPage(usersSearchInputId, page);
            } else {
                if (page === null) {
                    page = usersPage;
                } else {
                    updateContactsSelectorData(contactsSelectorUuid, { usersPage: page });
                }

                setDataLoading(true);

                /**
                 * @type {ContactsSearchOptions}
                 */
                const searchOptions = {
                    applyUsersListFilters: options.applyUsersListFilters,
                };

                if (!!options.users.restrictions.onlyHumans) {
                    searchOptions.onlyHumans = true;
                }

                searchContacts(
                    'subscribers',
                    offsetFromPage(page, options.recordsOnPage),
                    options.recordsOnPage,
                    searchOptions,
                ).then((data) => {
                    setLoadedUsers([...data.msisdns]);
                    setTotalUsers(data.totalRecordsCount);
                    setDataLoading(false);
                });
            }
        }

        if (currentTab === TabTypes.GROUPS_TAB) {
            if (options.searchOnlyMode && (!searchDataGroups || searchDataGroups.keyword.length == 0)) {
                return;
            }

            if (options.groups.availableGroupsIds) {
                if (options.groups.availableGroupsIds.length === 0) {
                    return;
                }

                setDataLoading(true);

                findGroupsByIdsList(options.groups.availableGroupsIds).then((data) => {
                    setLoadedGroups([...data.groupsIds]);
                    setTotalGroups(data.totalRecordsCount);
                    setDataLoading(false);
                });

                return;
            }

            if (searchDataGroups && searchDataGroups.keyword.length > 0) {
                if (page === null) {
                    page = searchDataGroups.page;
                }

                requestSearchInputPage(groupsSearchInputId, page);
            } else {
                if (page === null) {
                    page = groupsPage;
                } else {
                    updateContactsSelectorData(contactsSelectorUuid, { groupsPage: page });
                }

                setDataLoading(true);

                searchContacts('groups', offsetFromPage(page, options.recordsOnPage), options.recordsOnPage).then(
                    (data) => {
                        setLoadedGroups(data.groupsIds);
                        setTotalGroups(data.totalRecordsCount);
                        setDataLoading(false);
                    },
                );
            }
        }
    };

    /**
     * The tab change function.
     * @param {TabTypes} selectedTab
     * @param {boolean} loadContacts
     */
    const handleTabChange = (selectedTab, loadContacts = true) => {
        setCurrentTab(selectedTab);

        if (options.onTabChanged) {
            options.onTabChanged(selectedTab);
        }
    };

    /**
     * @param {CugContact} contact
     * @param {boolean} state
     */
    const handleContactSelectionChange = (contact, state) => {
        let newUsersSelection = [...currentUsersSelection];
        let newGroupsSelection = [...currentGroupsSelection];

        if (!contact.IsGroup) {
            if (state) {
                if (options.emulateRadioGroup) {
                    newUsersSelection = [contact.ContactId];
                } else {
                    newUsersSelection.push(contact.ContactId);
                }
            } else {
                newUsersSelection = newUsersSelection.filter((selectedId) => selectedId !== contact.ContactId);
            }

            setCurrentUsersSelection(newUsersSelection);
        } else {
            if (state) {
                if (options.emulateRadioGroup) {
                    newGroupsSelection = [contact.ContactId];
                } else {
                    newGroupsSelection.push(contact.ContactId);
                }
            } else {
                newGroupsSelection = newGroupsSelection.filter((selectedId) => selectedId !== contact.ContactId);
            }

            setCurrentGroupsSelection(newGroupsSelection);
        }

        // provide to output all selected contacts, sorted
        setTimeout(() => {
            propagateSelectionChangedData({
                msisdns: sortMsisdnsList(newUsersSelection),
                groupsIds: sortGroupsIdsList(newGroupsSelection),
                lastChangeData: {
                    contact: contact,
                    state: state,
                },
            });
        });
    };

    const renderUsers = () => {
        if (currentTab !== TabTypes.USERS_TAB) {
            return '';
        }

        // component not yet initialized
        if (usersSearchInputId === null) {
            return <Preloader></Preloader>;
        }

        if (searchInputHasNoResults(searchDataUsers)) {
            return <div className="no-search-results">{t('js_all_no_results')}</div>;
        }

        // search in progress
        if (
            searchDataUsers &&
            (searchDataUsers.userIsTyping || searchDataUsers.requestInProgress) &&
            contactsIdsListIsEmpty(searchDataUsers.matches)
        ) {
            return <div className="no-search-results">{t('js_all_search_searching')}</div>;
        }

        if (
            options.searchOnlyMode &&
            !(searchDataUsers.userIsTyping || searchDataUsers.requestInProgress) &&
            contactsIdsListIsEmpty(searchDataUsers.matches)
        ) {
            return <div className="no-search-results">{t('manage_user_groups_search_multiple_users')}</div>;
        }

        let users;
        let totalCount;
        let page;

        if (!options.users.availableMsisdns) {
            if (searchDataUsers.matches.msisdns.length > 0) {
                users = searchDataUsers.matches.msisdns;
                totalCount = searchDataUsers.matches.totalRecordsCount;
            } else {
                users = loadedUsers || [];
                totalCount = totalUsers;
            }
            page = searchDataUsers.keyword.length > 0 ? searchDataUsers.page : usersPage;
        } else {
            users =
                searchDataUsers.matches.msisdns.length > 0
                    ? searchDataUsers.matches.msisdns.filter((match) => options.users.availableMsisdns.includes(match))
                    : options.users.availableMsisdns;
            totalCount = users.length;
            page = 1;
        }

        // no users at all
        if (loadedUsers !== null && users.length === 0) {
            return <div className="no-search-results">{t('js_all_mybusiness_reporting_filter_users_no_users')}</div>;
        }

        const checkboxDisabled = (user) => {
            if (
                options.users.ignoredMsisdns.includes(user.Msisdn) ||
                options.users.restrictions.ignoredMsisdns?.includes(user.Msisdn)
            ) {
                return true;
            }

            if (options.lockSelectedContacts && options.users.selectedMsisdns.includes(user.Msisdn)) {
                return true;
            }

            return false;
        };

        const pagesCount = Math.ceil(totalCount / options.recordsOnPage);
        let renderedUsersCount = 0;

        return (
            <>
                {users.map(
                    /**
                     * @param {CugSubscriber|string} userOrMsisdn
                     * @return {JSX.Element}
                     */
                    (userOrMsisdn) => {
                        const user =
                            typeof userOrMsisdn !== 'object' ? getOrLoadUserDetails(userOrMsisdn) : userOrMsisdn;

                        // user is under request (the false points to that)
                        if (user === false) {
                            // still, show and empty user item
                            // adding items and keep changing the scroll height is annoying
                            return (
                                <div className="wcf-ui-contact-item disabled" key={`loading-user-${userOrMsisdn}`}>
                                    <Preloader asOverlay />
                                </div>
                            );
                        }

                        // user removed by CUG
                        if (user === null) {
                            return '';
                        }

                        if (!options.users.restrictions.allowGuests && user.IsGuest) {
                            return '';
                        }

                        renderedUsersCount++;

                        return (
                            <ContactUserItem
                                key={user.Msisdn}
                                msisdn={user.Msisdn}
                                withStatusDetails={options.users.renderingOptions.withStatusDetails}
                                withFavoriteControls={options.withFavoriteControls}
                                menuType={options.menuType}
                                multiSelectActive={options.multipleSelectionOpen}
                                multiSelectState={
                                    currentUsersSelection.includes(user.Msisdn) ||
                                    (options.lockSelectedContacts &&
                                        options.users.selectedMsisdns.includes(user.Msisdn))
                                }
                                multiSelectDisableState={checkboxDisabled(user)}
                                multiSelectOnChange={(c, s) => handleContactSelectionChange(c, s)}
                                nameClickCallback={(c) => options.onContactNameClick(c)}
                            ></ContactUserItem>
                        );
                    },
                )}
                {pagesCount > 1 && (
                    <div className="pagination-container">
                        <Pagination
                            color="primary"
                            className={`${pagesCount > 5 ? 'expanded' : ''}`}
                            count={pagesCount}
                            page={page}
                            onChange={(event, page) => handleRecordsPage(page)}
                        />
                    </div>
                )}
                {loadedUsers !== null && renderedUsersCount === 0 && (
                    <div className="no-search-results">{t('js_all_mybusiness_reporting_filter_users_no_users')}</div>
                )}
            </>
        );
    };

    const renderGroups = () => {
        if (currentTab !== TabTypes.GROUPS_TAB) {
            return '';
        }

        if (groupsSearchInputId === null) {
            return <Preloader></Preloader>;
        }

        if (searchInputHasNoResults(searchDataGroups)) {
            return <div className="no-search-results">{t('js_all_no_results')}</div>;
        }

        // search in progress
        if (
            searchDataGroups &&
            (searchDataGroups.userIsTyping || searchDataGroups.requestInProgress) &&
            contactsIdsListIsEmpty(searchDataGroups.matches)
        ) {
            return <div className="no-search-results">{t('js_all_search_searching')}</div>;
        }

        if (
            options.searchOnlyMode &&
            !(searchDataGroups.userIsTyping || searchDataGroups.requestInProgress) &&
            contactsIdsListIsEmpty(searchDataGroups.matches)
        ) {
            return <div className="no-search-results">{t('manage_user_groups_search_multiple_groups')}</div>;
        }

        let groups;
        let totalCount;
        let page;

        if (!options.groups.availableGroupsIds) {
            if (searchDataGroups.matches.groupsIds.length > 0) {
                groups = searchDataGroups.matches.groupsIds;
                totalCount = searchDataGroups.matches.totalRecordsCount;
            } else {
                groups = loadedGroups || [];
                totalCount = totalGroups;
            }
            page = searchDataGroups.keyword.length > 0 ? searchDataGroups.page : groupsPage;
        } else {
            groups =
                searchDataGroups.matches.groupsIds.length > 0
                    ? searchDataGroups.matches.groupsIds.filter((match) =>
                          options.groups.availableGroupsIds.includes(match),
                      )
                    : options.groups.availableGroupsIds;
            totalCount = groups.length;
            page = 1;
        }

        if (loadedGroups !== null && groups.length === 0) {
            return <div className="no-search-results">{t('js_all_no_results')}</div>;
        }

        const pagesCount = Math.ceil(totalCount / options.recordsOnPage);

        return (
            <>
                {groups.map(
                    /**
                     * @param {CugGroup|string} groupOrGroupId
                     * @return {JSX.Element}
                     */
                    (groupOrGroupId) => {
                        // there can be IDS from the selector or entities from the search
                        const groupId = typeof groupOrGroupId === 'object' ? groupOrGroupId.GroupId : groupOrGroupId;

                        const checkboxDisabled = () => {
                            if (
                                options.groups.restrictions.transmissionNotDenied === true &&
                                groupHasTransmissionDenied(groupId)
                            ) {
                                return true;
                            }

                            if (options.lockSelectedContacts && options.groups.selectedGroupsIds.includes(groupId)) {
                                return true;
                            }

                            return false;
                        };

                        return (
                            <ContactGroupItem
                                key={groupId}
                                groupId={groupId}
                                withFavoriteControls={options.withFavoriteControls}
                                menuType={options.menuType}
                                multiSelectActive={options.multipleSelectionOpen}
                                multiSelectState={
                                    currentGroupsSelection.includes(groupId) ||
                                    (options.lockSelectedContacts && options.groups.selectedGroupsIds.includes(groupId))
                                }
                                multiSelectDisableState={checkboxDisabled()}
                                multiSelectOnChange={(c, s) => handleContactSelectionChange(c, s)}
                                nameClickCallback={(c) => options.onContactNameClick(c)}
                            ></ContactGroupItem>
                        );
                    },
                )}
                {searchDataGroups.matches.groupsIds.length === 0 && pagesCount > 1 && (
                    <div className="pagination-container">
                        <Pagination
                            color="primary"
                            className={`${pagesCount > 5 ? 'expanded' : ''}`}
                            count={pagesCount}
                            page={page}
                            onChange={(event, page) => handleRecordsPage(page)}
                        />
                    </div>
                )}
            </>
        );
    };

    return (
        <div className="contacts-selector" data-selector-id={contactsSelectorUuid}>
            <div className="header-container">
                <div className="search-input-container">
                    {currentTab === TabTypes.USERS_TAB && (
                        <ContactSearchInput
                            uuid={usersSearchInputId}
                            contactType={'subscribers'}
                            renderContacts={false}
                            onlyHumans={options.users.restrictions.onlyHumans}
                            placeholder={t('manage_user_groups_search_multiple_users')}
                            applyUsersListFilters={options.applyUsersListFilters}
                            data-test-id="contacts-search-input"
                            recordsOnPage={options.recordsOnPage}
                            onReset={() => {
                                if (options.searchOnlyMode) {
                                    propagateSelectionChangedData({ msisdns: [] });
                                }
                            }}
                        />
                    )}
                    {currentTab === TabTypes.GROUPS_TAB && (
                        <ContactSearchInput
                            uuid={groupsSearchInputId}
                            contactType={'groups'}
                            renderContacts={false}
                            placeholder={t('manage_user_groups_search_multiple_groups')}
                            data-test-id="contacts-search-input"
                            recordsOnPage={options.recordsOnPage}
                            onReset={() => {
                                if (options.searchOnlyMode) {
                                    propagateSelectionChangedData({ groupsIds: [] });
                                }
                            }}
                        />
                    )}
                </div>
                {additionalContent && <div className="additional-content-container">{additionalContent}</div>}
                {!options.displayOnlyUsers && (
                    <div className="tabs-container">
                        <ContactsTabsSystem
                            currentTab={currentTab}
                            handleTabChange={(selectedTab) => handleTabChange(selectedTab)}
                        />
                    </div>
                )}
            </div>
            <div className="contacts-container">
                {dataLoading && <Preloader asOverlay />}
                {renderUsers()}
                {renderGroups()}
            </div>
        </div>
    );
}
